import React, { ComponentType } from 'react';
import { PuzzleCheckbox } from "puzzle";
import {
  GridCellProps,
  GridColumn as Column,
  GridColumnMenuProps,
  GridSortChangeEvent
} from "@progress/kendo-react-grid";
import { CLAIM_DIALOG_STATE, CLAIM_MENU, INSTRUCTIONS_DATA, RESPONSE } from './Types';
import { orderBy } from '@progress/kendo-data-query';
import { defaultCellRender } from "../../../util/Util";
import { CLAIM_DIALOG_DATA_ITEM } from "../../../Abstract/ClaimDialog/Types";
import ClaimDialogAbstract from "../../../Abstract/ClaimDialog/ClaimDialogAbstract";
import KendoGridAbstract from "../../../util/KendoGridAbstract/KendoGridAbstract";
import * as _ from "lodash";
import ColumnMenu from "puzzle/components/grid/columnmenu/ColumnMenu";

class ClaimDialog extends ClaimDialogAbstract<{}, CLAIM_DIALOG_STATE> {

  static defaultProps = {
    rowHeight: 48
  };

  public state: CLAIM_DIALOG_STATE = {
    claimLevel: 'BATCH',
    data: [],
    instructionsData: null,
    lastSelectedIndex: 0,
    selectAll: false,
    sort: [{
      field: 'targetLanguageDisplayName',
      dir: 'asc'
    }, {
      field: 'batchName',
      dir: 'asc'
    }, {
      field: 'phaseName',
      dir: 'asc'
    }],
    showClaimAndOpen: false,
    skip: 0
  };

  /**
   * Returns true if any record is checked, false if not.
   */
  checkForSelectedData = (): boolean => this.state.data.some(item => item.selected);

  /**
   * Claim grid cell render
   * @param defaultRendering
   * @param cellProps
   */
  claimCellRender = (defaultRendering: React.ReactElement<HTMLTableCellElement> | null, cellProps: GridCellProps):
    JSX.Element => {
    const { dataItem, field } = cellProps;
    let children;
    let editedProps = {
      ...defaultRendering?.props,
      className: defaultRendering?.props.className
    };
    if (defaultRendering && field) {
      switch (cellProps.field) {
        case 'status':
          children = this.statusCellRender(cellProps);
          break;
        case 'batchName':
        case 'targetLanguageDisplayName':
          children = defaultCellRender(this.translate, () => null, cellProps);
          break;
        case 'startDate':
        case 'dueDate':
          children = this.dateRender(field, cellProps);
          break;
        case 'wordCount':
          children = this.wordCountRender(cellProps);
          break;
        case 'select':
          editedProps.className += ' tp-select-file';
          children = this.selectCellRender(cellProps);
          break;
        default:
          children = dataItem[field];
          break;
      }
    }
    return React.cloneElement((defaultRendering as any), editedProps, children);
  };

  /**
   * Returns instructions data
   */
  getInstructionsData = (): null | string => this.state.instructionsData;

  /**
   * Handles sorting
   * @param e : GridSortChangeEvent
   */
  onSortChange = (e: GridSortChangeEvent) => {
    const { sort } = e;
    const { data } = this.state;

    let sortedData = _.orderBy(data, [sort[0].field], [sort[0].dir as "asc" | "desc"]);
    this.setState({
      data: sortedData,
      sort
    });
  };

  /**
   * Opens word count dialog
   */
  openWordCount = () => {
    this.dispatch('openWordCount', '', {
      downloadWordCountParams: {
        downloadTmStatistics: true
      },
      submission: this.props.submissionData,
    });
  };

  /**
   * Handles source files download
   */
  proceedWithClaim = () => {
    const { data } = this.state;
    let claimParams: any = [];
    let phaseIndex = -1;
    let currentItem;
    let items = 0;

    for (let i = 0; i < data.length; i++) {
      currentItem = data[i];
      if (currentItem.selected) {
        items++;
        phaseIndex = -1;
        for (let j = 0; j < claimParams.length; j++) {
          if (claimParams[j].phaseName === currentItem.phaseName) {
            phaseIndex = j;
            break;
          }
        }

        if (phaseIndex > -1) {
          claimParams[phaseIndex].jobIds.push(currentItem.jobId)
        } else {
          claimParams.push({
            phaseName: currentItem.phaseName,
            jobIds: [currentItem.jobId]
          })
        }
      }
    }

    return {
      params: { actions: claimParams },
      items
    }
  };

  /**
   * Select all items handler
   */
  selectAllHandler = () => {
    let { data, selectAll } = this.state;

    data = data.map((item: any) => {
      return {
        ...item,
        selected: !selectAll
      }
    });

    this.setState({
      data,
      selectAll: !selectAll
    }, this.updateFooter)
  };

  /**
   * Grid Selection Change handler
   * @param dataItem
   * @param event
   */
  selectionChange = (dataItem: CLAIM_DIALOG_DATA_ITEM, event: any): void => {
    let data = [...this.state.data];
    let last = this.state.lastSelectedIndex;
    const current = data.findIndex(item => item === dataItem);

    if (!event.nativeEvent.shiftKey) {
      last = current;
    }

    const select = !dataItem.selected;
    for (let i = Math.min(last, current); i <= Math.max(last, current); i++) {
      data[i].selected = select;
      if (this.state.claimLevel === "LANGUAGE") {
        data.forEach((item: CLAIM_DIALOG_DATA_ITEM) => {
          if ((item.jobId === dataItem.jobId && item.phaseName === dataItem.phaseName) || (item.targetLanguage === data[i].targetLanguage && item.phaseName === data[i].phaseName)) {
            item.selected = select
          }
        })
      }
    }

    let selectAll = data.every((item: any) => item.selected);
    this.setState({
      data,
      selectAll,
      lastSelectedIndex: last
    }, this.updateFooter)
  };

  /**
   * Sets Source files grid data.
   * @param claimData
   * @param instructionsData
   */
  setData = ({ claimableItems: data, claimLevel }: RESPONSE, instructionsData: INSTRUCTIONS_DATA): void => {
    let { sort } = this.state;
    if (sort) {
      data = orderBy(data, sort);
    }
    this.setState({
      claimLevel,
      data,
      instructionsData: instructionsData.instructions
    }, () => {
      if (data.length === 1) {
        this.selectAllHandler();
      }
    })
  };

  /**
   * Selection cell render
   * @param cellProps
   */
  selectCellRender = (cellProps: GridCellProps): JSX.Element => {
    const { dataItem } = cellProps;
    return (
      <PuzzleCheckbox
        checked={!!dataItem['selected']}
        className={`${dataItem.selected ? 'qa-fakeClass-checked' : ''}`}
        onChange={this.selectionChange.bind(this, dataItem)}
        label={this.translate('SRL-checkboxLabel', {
          language: dataItem.targetLanguageDisplayName,
          batch: dataItem.batchName,
          phase: dataItem.phaseName
        })}
      />
    )
  };

  /**
   * Select all checkbox render
   */
  selectAllRender = (): JSX.Element => {
    const { selectAll } = this.state;
    return (
      <PuzzleCheckbox
        checked={selectAll}
        className={`${selectAll ? 'qa-fakeClass-checked' : ''}`}
        label={this.translate('selectAllLabel')}
        onChange={this.selectAllHandler}
      />
    )
  };

  /**
   * Performs footer update on current dialog
   */
  updateFooter = () => {
    this.dispatch('updateClaimFooter', '', {
      existingSelection: this.state.data.some((item: any) => item.selected),
      instructions: this.state.instructionsData
    });
  };

  /**
   * Column menu render
   * @param props
   */
  renderColumnMenu = (props: GridColumnMenuProps): JSX.Element => {
    const columnName: string = props.column.field as string;
    let columnMenuProps: CLAIM_MENU = {
      closeMenu: props.onCloseMenu as Function,
      sortable: true,
      onSortChange: this.onSortChange,
      filterable: false
    };

    return <ColumnMenu
      column={{ field: columnName }}
      {...columnMenuProps}
    />
  };

  renderBody = () => {
    const { data, sort, skip } = this.state;
    const { rowHeight } = this.props;
    const columnMenu: { columnMenu: ComponentType<GridColumnMenuProps>, minResizableWidth: number } = {
      columnMenu: this.renderColumnMenu,
      minResizableWidth: 100
    };

    return (
      <>
        <KendoGridAbstract
          captionText={this.translate('SRL-claimTableCaption')}
          resizable={true}
          rowHeight={rowHeight}
          data={data.slice(skip, skip + 20)}
          scrollable={'virtual'}
          pageSize={20}
          sortable
          sort={sort}
          cellRender={this.claimCellRender}
          skip={skip}
          total={data.length}
        >
          <Column
            field={"select"}
            filterable={false}
            headerCell={this.selectAllRender}
            width={46}
            resizable={false}
          />
          <Column
            field="status"
            filterable={false}
            headerClassName={'tp-sortable'}
            className={'tp-sortable'}
            title={this.translate('columnHeaders.status')}
            width="120px"
            {...columnMenu}
          />
          <Column
            field="targetLanguageDisplayName"
            filterable={false}
            headerClassName={'tp-sortable'}
            className={'tp-sortable'}
            title={this.translate('columnHeaders.targetLanguageDisplayName')}
            width="190px"
            {...columnMenu}
          />
          <Column
            field="batchName"
            filterable={false}
            headerClassName={'tp-sortable'}
            className={'tp-sortable'}
            title={this.translate('columnHeaders.batchName')}
            width="190px"
            {...columnMenu}
          />
          <Column
            field="phaseName"
            filterable={false}
            headerClassName={'tp-sortable'}
            className={'tp-sortable'}
            title={this.translate('columnHeaders.phaseName')}
            width="170px"
            {...columnMenu}
          />
          <Column
            field="startDate"
            filterable={false}
            headerClassName={'tp-sortable'}
            className={'tp-sortable'}
            title={this.translate('columnHeaders.startDate')}
            width="170px"
            {...columnMenu}
          />
          <Column
            field="dueDate"
            filterable={false}
            headerClassName={'tp-sortable'}
            className={'tp-sortable'}
            title={this.translate('columnHeaders.dueDate')}
            width="170px"
            {...columnMenu}
          />
          <Column
            field="wordCount"
            filterable={false}
            sortable={true}
            className={'tp-sortable'}
            headerClassName={'tp-sortable'}
            title={this.translate('columnHeaders.wordCount')}
            width="150px"
            {...columnMenu}
          />
        </KendoGridAbstract>
      </>
    );
  }
}

export default ClaimDialog;
